<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useD4HStore } from '@/stores/d4h'
import { useSyncStore } from '@/stores/sync'
import { useToastStore } from '@/stores/toast'
import {
  D4H_QUAL_FIELDS,
  MEDICAL_QUAL_OPTIONS,
  SEARCHER_TYPE_OPTIONS,
  ROPE_QUAL_OPTIONS,
  STANDARD_MISC_QUALS,
  type D4HQualField,
  type D4HQualMappingEntry,
  type D4HQualification,
} from '@/types'
import { suggestQualMapping, splitQualTitle } from '@/utils/d4hQuals'
import FosDialog from '@/components/ui/FosDialog.vue'
import FosButton from '@/components/ui/FosButton.vue'
import FosInput from '@/components/ui/FosInput.vue'
import FosSelect from '@/components/ui/FosSelect.vue'

const props = defineProps<{
  open: boolean
  // Agency.custom_quals, offered alongside STANDARD_MISC_QUALS
  customQuals?: string[]
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
}>()

const d4hStore = useD4HStore()
const syncStore = useSyncStore()
const toast = useToastStore()

type Filter = 'all' | 'unmapped' | 'mapped' | 'suggested'

const draft = ref<Record<string, D4HQualMappingEntry>>({})
const search = ref('')
const filter = ref<Filter>('all')
const collapsed = ref<Set<string>>(new Set())
const loading = ref(false)
const saving = ref(false)

const offline = computed(() => !syncStore.isOnline)

const UNMAPPED = '__none__'
const FIELD_LABELS: Record<D4HQualField, string> = {
  medical_qual: 'Medical',
  searcher_type: 'Searcher Type',
  rope_qual: 'Rope',
  misc_quals: 'Other',
}

function valueOptions(field: D4HQualField): string[] {
  switch (field) {
    case 'medical_qual': return MEDICAL_QUAL_OPTIONS
    case 'searcher_type': return SEARCHER_TYPE_OPTIONS
    case 'rope_qual': return ROPE_QUAL_OPTIONS
    case 'misc_quals': return [...STANDARD_MISC_QUALS, ...(props.customQuals ?? [])]
  }
}

// One select per row: "Medical · EMT". Values encode field|value.
const mappingOptions = computed(() => [
  { label: 'Not mapped', value: UNMAPPED },
  ...D4H_QUAL_FIELDS.flatMap((field) =>
    valueOptions(field)
      .filter((v) => v !== 'None')
      .map((v) => ({ label: `${FIELD_LABELS[field]} · ${v}`, value: `${field}|${v}` })),
  ),
])

function entryLabel(entry: D4HQualMappingEntry): string {
  return `${FIELD_LABELS[entry.field]} · ${entry.value}`
}

function setMapping(title: string, encoded: string) {
  const rest = { ...draft.value }
  delete rest[title]
  if (encoded === UNMAPPED) {
    draft.value = rest
    return
  }
  const [field, ...valueParts] = encoded.split('|')
  draft.value = { ...rest, [title]: { field: field as D4HQualField, value: valueParts.join('|') } }
}

// ── Rows ───────────────────────────────────────────────────────────────────────

interface Row extends D4HQualification {
  group: string
  name: string
  suggestion: D4HQualMappingEntry | null
}

const rows = computed<Row[]>(() =>
  d4hStore.qualifications.map((q) => ({
    ...q,
    ...splitQualTitle(q.title),
    suggestion: suggestQualMapping(q.title, props.customQuals),
  })),
)

const isMapped = (r: Row) => Boolean(draft.value[r.title])
const hasSuggestion = (r: Row) => !isMapped(r) && r.suggestion !== null

const counts = computed(() => ({
  all: rows.value.length,
  mapped: rows.value.filter(isMapped).length,
  unmapped: rows.value.filter((r) => !isMapped(r)).length,
  suggested: rows.value.filter(hasSuggestion).length,
}))

const filters: { key: Filter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'unmapped', label: 'Unmapped' },
  { key: 'mapped', label: 'Mapped' },
  { key: 'suggested', label: 'Suggested' },
]

const visibleRows = computed(() => {
  const q = search.value.trim().toLowerCase()
  return rows.value.filter((r) => {
    if (q && !r.title.toLowerCase().includes(q)) return false
    switch (filter.value) {
      case 'mapped': return isMapped(r)
      case 'unmapped': return !isMapped(r)
      case 'suggested': return hasSuggestion(r)
      default: return true
    }
  })
})

const groups = computed(() => {
  const byGroup = new Map<string, Row[]>()
  for (const row of visibleRows.value) {
    const list = byGroup.get(row.group) ?? []
    list.push(row)
    byGroup.set(row.group, list)
  }
  // Untitled group (titles without a category prefix) goes last
  return [...byGroup.entries()]
    .sort(([a], [b]) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b)))
    .map(([name, items]) => ({ name, items, mapped: items.filter(isMapped).length }))
})

function isCollapsed(group: string): boolean {
  // Searching expands everything so matches aren't hidden
  return !search.value.trim() && collapsed.value.has(group)
}

function toggleGroup(group: string) {
  const next = new Set(collapsed.value)
  if (next.has(group)) next.delete(group)
  else next.add(group)
  collapsed.value = next
}

const visibleSuggestions = computed(() => visibleRows.value.filter(hasSuggestion))

function acceptSuggestion(row: Row) {
  if (row.suggestion) draft.value = { ...draft.value, [row.title]: { ...row.suggestion } }
}

function acceptVisibleSuggestions() {
  const next = { ...draft.value }
  for (const row of visibleSuggestions.value) next[row.title] = { ...row.suggestion! }
  draft.value = next
}

// Mappings for titles D4H no longer returns. Kept on save, in case the qual comes back.
const staleCount = computed(() => {
  const titles = new Set(d4hStore.qualifications.map((q) => q.title))
  return Object.keys(draft.value).filter((t) => !titles.has(t)).length
})

const dirty = computed(() => {
  const saved = d4hStore.config?.qual_mapping ?? {}
  const keys = new Set([...Object.keys(saved), ...Object.keys(draft.value)])
  for (const k of keys) {
    const a = saved[k]
    const b = draft.value[k]
    if (a?.field !== b?.field || a?.value !== b?.value) return true
  }
  return false
})

// ── Lifecycle ──────────────────────────────────────────────────────────────────

async function loadQuals() {
  loading.value = true
  try {
    await d4hStore.loadQualifications()
  } catch (err) {
    toast.add(err instanceof Error ? err.message : 'Could not load D4H qualifications', 'error', 4000)
  } finally {
    loading.value = false
  }
}

watch(() => props.open, (open) => {
  if (!open) return
  draft.value = { ...(d4hStore.config?.qual_mapping ?? {}) }
  search.value = ''
  filter.value = 'all'
  if (d4hStore.qualifications.length === 0 && !offline.value) loadQuals()
}, { immediate: true })

async function save() {
  saving.value = true
  try {
    await d4hStore.saveQualMapping(draft.value)
    toast.add('Qualification mapping saved. Sync to apply it to the roster.', 'success', 4000)
    emit('update:open', false)
  } catch (err) {
    toast.add(err instanceof Error ? err.message : 'Could not save qualification mapping', 'error', 4000)
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <FosDialog
    :open="open"
    max-width="sm:max-w-4xl h-[85vh]"
    body-class="flex flex-col overflow-hidden"
    @update:open="emit('update:open', $event)"
  >
    <template #title>
      <div class="flex flex-col gap-1">
        <span class="font-headline font-black text-lg uppercase tracking-tight text-on-surface">Qualification Mapping</span>
        <span class="text-xs font-mono text-on-surface-variant normal-case">
          {{ d4hStore.config?.team_name || 'D4H' }} · {{ counts.all }} qualifications.
          Members get the FoS qualification each D4H qualification maps to.
        </span>
      </div>
    </template>

    <!-- Toolbar -->
    <div class="flex flex-wrap items-center gap-2 pb-3 shrink-0">
      <div class="flex-1 min-w-[14rem]">
        <FosInput v-model="search" placeholder="Search D4H qualifications" />
      </div>
      <div class="flex border border-border rounded overflow-hidden" role="group" aria-label="Filter">
        <button
          v-for="f in filters"
          :key="f.key"
          type="button"
          :aria-pressed="filter === f.key"
          :class="[
            'px-2.5 py-2 text-[10px] font-label uppercase tracking-widest border-r border-border last:border-0 transition-colors',
            filter === f.key
              ? 'bg-primary text-on-primary'
              : 'bg-surface-container-high text-on-surface-variant hover:text-on-surface',
          ]"
          @click="filter = f.key"
        >
          {{ f.label }} <span class="font-mono">{{ counts[f.key] }}</span>
        </button>
      </div>
      <FosButton
        variant="tertiary"
        size="sm"
        :disabled="offline || loading"
        @click="loadQuals"
      >
        Refresh
      </FosButton>
    </div>

    <div
      v-if="visibleSuggestions.length > 0"
      class="flex items-center justify-between gap-2 px-3 py-2 mb-3 border border-border rounded bg-surface-container-high shrink-0"
    >
      <span class="text-xs text-on-surface-variant">
        <span class="font-mono text-on-surface">{{ visibleSuggestions.length }}</span>
        unmapped {{ visibleSuggestions.length === 1 ? 'qualification has' : 'qualifications have' }}
        a suggested mapping based on the title. Review before saving.
      </span>
      <FosButton variant="secondary" size="sm" @click="acceptVisibleSuggestions">
        Accept {{ visibleSuggestions.length }}
      </FosButton>
    </div>

    <!-- List -->
    <div class="flex-1 min-h-0 overflow-y-auto border border-border rounded">
      <p v-if="loading && rows.length === 0" class="px-3 py-3 text-xs text-on-surface-variant font-mono">
        Loading D4H qualifications…
      </p>
      <p v-else-if="offline && rows.length === 0" class="px-3 py-3 text-xs text-on-surface-variant font-mono">
        Offline. Loading D4H qualifications needs a connection.
      </p>
      <p v-else-if="groups.length === 0" class="px-3 py-3 text-xs text-on-surface-variant font-mono">
        {{ rows.length === 0 ? 'No D4H qualifications found.' : 'No qualifications match.' }}
      </p>

      <section v-for="group in groups" :key="group.name">
        <button
          type="button"
          class="sticky top-0 z-10 w-full flex items-center gap-2 px-3 py-2 bg-surface-container-high border-b border-border text-left"
          :aria-expanded="!isCollapsed(group.name)"
          @click="toggleGroup(group.name)"
        >
          <span class="material-symbols-outlined text-[18px] text-outline">
            {{ isCollapsed(group.name) ? 'chevron_right' : 'expand_more' }}
          </span>
          <span class="flex-1 truncate text-[10px] font-label uppercase tracking-widest text-on-surface">
            {{ group.name || 'Ungrouped' }}
          </span>
          <span class="text-xs font-mono text-on-surface-variant">{{ group.mapped }}/{{ group.items.length }} mapped</span>
        </button>

        <template v-if="!isCollapsed(group.name)">
          <div
            v-for="row in group.items"
            :key="row.title"
            class="grid grid-cols-[1fr_4.5rem_15rem] items-center gap-3 px-3 py-2 border-b border-border"
          >
            <div class="min-w-0">
              <div
                :class="['text-sm truncate', draft[row.title] ? 'text-on-surface' : 'text-on-surface-variant']"
                :title="row.title"
              >
                {{ row.name }}
              </div>
              <div v-if="hasSuggestion(row)" class="flex items-center gap-2 mt-0.5">
                <span class="text-[11px] font-mono text-outline truncate">Suggested: {{ entryLabel(row.suggestion!) }}</span>
                <button
                  type="button"
                  class="text-[10px] font-label uppercase tracking-widest text-primary hover:underline shrink-0"
                  @click="acceptSuggestion(row)"
                >
                  Accept
                </button>
              </div>
            </div>
            <span
              :class="['text-xs font-mono text-right', row.holders ? 'text-on-surface' : 'text-outline']"
              :title="`${row.holders} current ${row.holders === 1 ? 'member holds' : 'members hold'} this qualification`"
            >
              {{ row.holders }} <span class="text-outline">held</span>
            </span>
            <FosSelect
              :model-value="draft[row.title] ? `${draft[row.title]!.field}|${draft[row.title]!.value}` : UNMAPPED"
              :options="mappingOptions"
              :disabled="offline"
              @update:model-value="setMapping(row.title, $event)"
            />
          </div>
        </template>
      </section>
    </div>

    <template #footer>
      <span class="mr-auto self-center text-xs font-mono text-on-surface-variant">
        <span class="text-on-surface">{{ counts.mapped }}</span> of {{ counts.all }} mapped
        <template v-if="staleCount"> · {{ staleCount }} kept for qualifications no longer in D4H</template>
      </span>
      <FosButton variant="secondary" @click="emit('update:open', false)">Cancel</FosButton>
      <FosButton variant="primary" :disabled="offline || saving || !dirty" @click="save">
        {{ saving ? 'Saving…' : 'Save Mapping' }}
      </FosButton>
    </template>
  </FosDialog>
</template>
